import React, { useEffect, useState } from "react";
import { setAiConsent } from "../lib/api";

/**
 * AI consent modal — triggered globally when any `/api/ai/*` route
 * returns `403 AI_CONSENT_REQUIRED` and the user hasn't yet granted
 * consent.
 *
 * The modal is mounted once at the App root and listens on the global
 * window event `lh:ai-consent-required`. The axios interceptor dispatches
 * that event when it sees the 403, and the modal resolves the
 * `pendingRetry` promise on accept so the original caller can re-fire
 * its request transparently.
 *
 * One-shot UX: once the user accepts, future calls succeed
 * without re-prompting (the consent is persisted on the user doc).
 */
export default function AiConsentModal() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [resolver, setResolver] = useState(null);

  useEffect(() => {
    const handler = (ev) => {
      setErr("");
      setResolver(() => ev.detail?.onResolve || null);
      setOpen(true);
    };
    window.addEventListener("lh:ai-consent-required", handler);
    return () => window.removeEventListener("lh:ai-consent-required", handler);
  }, []);

  const accept = async () => {
    setBusy(true); setErr("");
    try {
      await setAiConsent(true);
      setOpen(false);
      // Resolve the original caller's promise so it can retry.
      if (typeof resolver === "function") resolver({ granted: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not record consent.");
    } finally {
      setBusy(false);
    }
  };

  const decline = () => {
    setOpen(false);
    if (typeof resolver === "function") resolver({ granted: false });
  };

  if (!open) return null;
  return (
    <div
      data-testid="ai-consent-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ai-consent-title"
      className="fixed inset-0 z-[180] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
    >
      <div className="w-full max-w-md rounded-2xl border border-[#14b8a6]/40 bg-[#0b1722] shadow-[0_0_60px_-15px_rgba(20,184,166,0.5)] p-5">
        <p
          id="ai-consent-title"
          className="text-[12px] tracking-widest text-[#14b8a6] font-bold"
        >
          AI PROCESSING CONSENT
        </p>
        <p className="mt-2 text-[12px] text-white/80 leading-snug">
          To use this feature, LEAGUE HUB will send your text/image
          prompt to one of our third-party AI providers
          (<strong>DeepSeek</strong>, <strong>OpenAI</strong>, or
          <strong>Gemini</strong>). The prompt may include any
          gameplay context you typed.
        </p>
        <ul className="mt-2 text-[11px] text-white/55 leading-snug list-disc pl-4 space-y-1">
          <li>You can revoke consent any time via Profile → PRIVACY.</li>
          <li>We never share your data for ad targeting.</li>
          <li>See our <a href="/legal/privacy" className="underline text-[#14b8a6]">Privacy Policy</a> for retention details.</li>
        </ul>
        {err && (
          <p
            data-testid="ai-consent-error"
            className="mt-3 text-[11px] text-red-300 bg-red-500/10 border border-red-400/40 rounded-lg p-2"
          >
            {err}
          </p>
        )}
        <div className="mt-4 flex justify-end gap-2 text-[10px] tracking-widest">
          <button
            type="button"
            onClick={decline}
            disabled={busy}
            data-testid="ai-consent-decline"
            className="px-3 py-1.5 rounded-lg bg-white/8 hover:bg-white/15 text-white"
          >
            NOT NOW
          </button>
          <button
            type="button"
            onClick={accept}
            disabled={busy}
            data-testid="ai-consent-accept"
            className="px-3 py-1.5 rounded-lg bg-[#14b8a6] text-black font-bold hover:bg-[#10a39a]"
          >
            {busy ? "SAVING…" : "GRANT CONSENT"}
          </button>
        </div>
      </div>
    </div>
  );
}
